/**
 * GET `pharmacy/questionnaire-review/{orderId}` — fetches the saved
 * questionnaire-review record for a single PA order.
 *
 * Wraps {@link API_ENDPOINTS.QUESTIONNAIRE_REVIEW} with bearer-auth headers,
 * supports cancellation via an optional {@link AbortSignal}, and surfaces
 * errors through the shared `logError` channel.
 *
 * @module api/getCalls/getQuestionnaireReviewForAnOrder
 */
import { QuestionnaireReviewResponse } from "../../data-model/questionnaireReview";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

/**
 * Fetches the questionnaire-review record for the supplied PA order.
 *
 * @param orderId - PA order id appended to the endpoint path
 *                  (e.g. `22390-0090` → `.../questionnaire-review/22390-0090`)
 * @param signal  - Optional `AbortSignal` used to cancel the in-flight request
 * @returns       Parsed {@link QuestionnaireReviewResponse} envelope
 * @throws        Rethrows any non-2xx HTTP error or abort error after logging
 */
export const getQuestionnaireReviewForAnOrder = async (
  orderId: string,
  signal?: AbortSignal,
): Promise<QuestionnaireReviewResponse> => {
  if (!orderId) {
    throw new Error("orderId is required to fetch questionnaire review");
  }

  const url = `${API_ENDPOINTS.QUESTIONNAIRE_REVIEW}${encodeURIComponent(orderId)}`;

  try {
    const headers = await getAuthHeaders();

    const requestOptions: RequestInit = {
      method: "GET",
      headers,
      redirect: "follow",
      signal,
    };

    const response = await fetch(url, requestOptions);
    const result = (await handleApiResponse(
      response,
    )) as QuestionnaireReviewResponse;
    logDataToConsole("getQuestionnaireReviewForAnOrder", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole("getQuestionnaireReviewForAnOrder", "Request aborted");
      throw error;
    }
    logError(error as Error, "Error in getQuestionnaireReviewForAnOrder");
    throw error;
  }
};
